import { Component, Fragment, h, Prop, State, Event, forceUpdate } from '@stencil/core';
import { User } from '../../global/models';
import { PermissionsSet } from '../../global/models/User';
import { PersonDoc, RfiDoc, RFISettingsDoc } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { RfiService } from 'src/RFIs/services/RfiService';
import { RfiCreateModal, RfiUtils } from '..';
import { ChangeEvent, DbListener } from 'src/global/ConcourseDb';
import { peopleState } from 'src/global';

@Component({
  tag: 'concourse-rfis',
  styleUrls: ['concourse-rfis.css', '../../global/app.css'],
  shadow: true,
})
export class ConcourseRfis {
  @Prop() projectId;
  @State() loading: boolean = false;
  @State() settings: RFISettingsDoc;
  @State() rfis: RfiDoc[] = [];
  @State() searchTerm: string;
  //@State() createModalOpen: boolean = false;
  //@State() currentRfi: RfiDoc = null;
  @State() mobileSearchOpen: boolean = false;

  @State() sortColumn: string = 'pos';
  @State() sortDir: string = 'asc';

  @Event({ bubbles: true }) requestAccess;
  private permissions: PermissionsSet;
  @State() errorMessage: string = '';

  get user(): User {
    return globalThis.user;
  }

  get people(): PersonDoc[] {
    return peopleState.people;
  }

  async componentWillLoad() {
    this.permissions = this.user.getRfiPermissions(this.projectId);
    if (this.permissions.canRead) {
      this.settings = await RfiService.getRfiSettings(this.projectId);
      await this.loadRfiData();
    } else {
      this.requestAccess.emit({ title: 'RFIs', role: this.permissions.readRole });
    }
  }

  /** Header searchbar passes the string value directly (see concourse-header `handleSearch`). */
  onSearch(value: string) {
    const v = value ?? '';
    if (v.length > 2 || v.length > 0) {
      this.searchTerm = v;
      if (!this.mobileSearchOpen) {
        this.mobileSearchOpen = true;
      }
    } else {
      this.searchTerm = null;
      this.mobileSearchOpen = false;
    }
  }

  async showSearchModal() {
    this.mobileSearchOpen = true;
  }

  onRfiSelectedModal(rfi: RfiDoc) {
    this.closeSearchModal();
    this.onRfiSelected(rfi);
  }

  closeSearchModal() {
    this.mobileSearchOpen = false;
  }

  async showCreateModal(rfiId: string = null) {
    if (!this.permissions.canWrite) {
      this.requestAccess.emit({ title: 'Create RFIs', role: this.permissions.writeRole });
      return;
    }

    const db = RfiService.getRfisDb(this.projectId);
    const states = this.settings?.states || RfiUtils.DEFAULT_RFI_SETTINGS.states;
    const rfi = rfiId ? await db.getDocById(rfiId) : RfiUtils.getBaseRfi(states);
    const saved = await RfiCreateModal.present(this.projectId, rfi);
    if (saved) {
      if (saved?.num) {
        // navigate to the rfi
        document.location.hash = `/project/${this.projectId}/rfi/${saved._id}`;
      } else {
        // When saving a draft, we don't pass back the rfiNumber, because its not confirmed till RFI is created.
        // So, we stay on the RFI list page and need to reload the rfis
        await this.loadRfiData();
      }
    }
  }

  private async loadRfiData() {
    const rfisDb = RfiService.getRfisDb(this.projectId);
    rfisDb.addListeners(this.rfisDbListener);
    await rfisDb.syncRemote();
  }

  private rfisDbListener: DbListener = {
    onComplete: (docs: RfiDoc[]) => {
      this.rfis = docs;
    },
    onChange: (event: ChangeEvent) => {
      this.rfis = event.docs;
    },
    onError: (e: Error) => {
      if (e.message.startsWith('Failed to fetch')) {
        // we have connectivity issues, but do we have rfis cached?
        //what
        console.log('failed to load rfis from remote', this.rfis);
      } else if (e.message == 'User does not exist on session') {
        window.location.hash = '/login';
      } else {
        this.errorMessage = e.message;
      }
    },
  };

  toggleTableView(viewTable: boolean) {
    const parts = document.location.hash.split('/');
    const clean = parts.slice(0, parts.indexOf('rfis') + 1);

    document.location.hash = clean.join('/').concat(viewTable ? '/columns' : '/table');
    forceUpdate(this);
  }

  async onRfisEdited(rfis: RfiDoc[]) {
    this.rfis = await RfiService.saveRfiList(this.projectId, rfis);
  }

  onRfiSelected(rfi: RfiDoc) {
    if (RfiUtils.isDraft(rfi)) this.showCreateModal(rfi._id);
    else document.location.hash = `/project/${this.projectId}/rfi/${rfi._id}`;
  }

  get rfisFiltered(): RfiDoc[] {
    if (!this.searchTerm) return this.rfis;

    // Search for RFI that contain the text
    const containsText: RfiDoc[] = this.rfis.filter(rfi => RfiUtils.contains(rfi, this.searchTerm));
    // Search for People that contain the text
    const peopleFiltered: PersonDoc[] = PeopleService.filterPeople(this.people, this.searchTerm);
    // Search for RFI that have the people found above.
    const containsPeople: RfiDoc[] = this.rfis.filter(rfi => RfiUtils.hasPeople(rfi, peopleFiltered));

    // In case someone typed in the exact state
    const state = this.settings.states.find(s => s.action?.includes(this.searchTerm));

    const isInState = state
      ? this.rfis.filter(rfi => rfi.status_history.some(completeState => completeState?.state_id == state?.id || completeState?.state_id?.includes(this.searchTerm)))
      : [];

    return [...containsText, ...containsPeople, ...isInState];
  }

  handleMobileSearchDismiss() {
    this.mobileSearchOpen = false;
    this.searchTerm = null;
  }

  async disconnectedCallback() {
    const rfidb = RfiService.getRfisDb(this.projectId);
    if (!rfidb) {
      return;
    }
    rfidb.removeListeners(this.rfisDbListener);
  }

  render() {
    const parts = document.location.hash.split('/');
    const view = parts[parts.indexOf('rfis') + 1];
    const viewTable = view == 'table';
    return (
      <Fragment>
        <concourse-header
          projectId={this.projectId}
          pageTitle="Rfis"
          shareRoles={[`p-${this.projectId}-rfis`]}
          showLogo={true}
          showHamburger={true}
          showSearchField={true}
          searchValue={this.searchTerm}
          onSearchInput={e => this.onSearch(e.detail)}
        >
          {/* Header: show Create for any user with RFIs read access (not only when Couch `settings` exists). Create modal still defaults states if needed. */}
          {this.permissions.canRead && (
            <div>
              {this.permissions.canWriteProject && (
                <ion-button href={'#/project/' + this.projectId + '/rfi/settings'}>
                  <ion-icon slot="icon-only" name="settings-outline"></ion-icon>
                </ion-button>
              )}
              {/* Table/columns toggle only when persisted settings exist (same as before). */}
              {this.settings && (
                <ion-button class="ion-hide-md-down" onClick={() => this.toggleTableView(viewTable)}>
                  <ion-icon slot="icon-only" src={viewTable ? '/assets/icon/columns-icon.svg' : '/assets/icon/table-icon.svg'}></ion-icon>
                </ion-button>
              )}
              {/* create-rfi-header: E2E desktop (ion-hide-md-down). */}
              <ion-button data-testid="create-rfi-header" color="primary" fill="solid" class="ion-hide-md-down" shape="round" onClick={() => this.showCreateModal()}>
                <ion-icon name="add-outline"></ion-icon>
                <ion-label>Create</ion-label>
              </ion-button>
            </div>
          )}
        </concourse-header>
        <ion-content scroll-x="true" scroll-y="true">
          <search-wrapper isSearching={this.mobileSearchOpen}>
            {this.loading ? (
              <ion-loading message="Loading RFIs..."></ion-loading>
            ) : !this.permissions.canRead ? (
              <div class="no-access-container">
                <p>No access to RFIs.</p>
                <p>
                  <a href={`#/project/${this.projectId}/dashboard`}>Back to Dashboard</a>
                </p>
              </div>
            ) : this.rfis.length <= 0 ? (
              <div class="center-content" style={{ height: '100%' }}>
                <div class="ion-text-center">
                  <p>No Rfis found</p>
                  {this.permissions.canWrite && (
                    <ion-button data-testid="create-rfi-empty-state" color="primary" fill="solid" shape="round" onClick={() => this.showCreateModal()}>
                      <ion-label>Create Rfi</ion-label>
                    </ion-button>
                  )}
                </div>
              </div>
            ) : this.settings ? (
              [
                <Fragment>
                  {viewTable ? (
                    <view-table
                      canWrite={this.permissions.canWrite}
                      rfis={this.rfisFiltered}
                      people={this.people}
                      settings={this.settings}
                      onRfisEdited={e => this.onRfisEdited(e.detail)}
                      onRfiSelected={e => this.onRfiSelected(e.detail)}
                    />
                  ) : (
                    <view-columns
                      canWrite={this.permissions.canWrite}
                      rfis={this.rfisFiltered}
                      people={this.people}
                      settings={this.settings}
                      onRfisEdited={e => this.onRfisEdited(e.detail)}
                      onRfiSelected={e => this.onRfiSelected(e.detail)}
                    />
                  )}
                </Fragment>,
                <ion-button
                  data-testid="create-rfi-fab"
                  color="primary"
                  fill="solid"
                  shape="round"
                  class="ion-hide-md-up floating-create-button safe-area-bottom"
                  onClick={() => this.showCreateModal()}
                >
                  <ion-label>Create</ion-label>
                  <ion-icon name="add-outline"></ion-icon>
                </ion-button>,
              ]
            ) : (
              /* Legacy fallback if `settings` becomes falsy at runtime (unlikely after load defaults). */
              <div class="no-access-container">
                No access to RFIs. <a href={`#/project/${this.projectId}/dashboard`}>Back to Dashboard</a>
              </div>
            )}

            <div slot="search-results">
              {this.rfisFiltered.map(rfi => {
                const assignees: PersonDoc[] = rfi.assignees ? PeopleService.findPeopleByIds(this.people, rfi.assignees) : [];
                return (
                  <ion-item class="modal-search-result" onClick={() => this.onRfiSelectedModal(rfi)}>
                    <ion-title>
                      #{rfi.num} : {rfi.subject}
                    </ion-title>
                    {assignees && (
                      <ion-chip class="chip">
                        <ion-avatar>
                          <img src={PeopleService.getGroupIcon(assignees)} />
                        </ion-avatar>
                        <ion-label>{PeopleService.getGroupName(assignees)}</ion-label>
                      </ion-chip>
                    )}
                  </ion-item>
                );
              })}
            </div>
          </search-wrapper>
        </ion-content>
      </Fragment>
    );
  }
}
